import type { User } from "@trader/shared/AuthApi"
import { useSyncExternalStore } from "react"
import { call, onUnauthorized } from "../../api.ts"

export type SessionState =
  | { readonly status: "loading" }
  | { readonly status: "signedOut" }
  | { readonly status: "signedIn"; readonly user: User }

let state: SessionState = { status: "loading" }
const listeners = new Set<() => void>()

const set = (next: SessionState) => {
  state = next
  for (const listener of listeners) listener()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useSession = (): SessionState => useSyncExternalStore(subscribe, () => state)

/** Asks the server who we are; called once at startup. */
export const restoreSession = (): void => {
  call((client) => client.auth.me()).then(
    (user) => set({ status: "signedIn", user }),
    () => set({ status: "signedOut" })
  )
}

export const login = async (username: string, password: string): Promise<void> => {
  const user = await call((client) => client.auth.login({ payload: { username, password } }))
  set({ status: "signedIn", user })
}

export const logout = async (): Promise<void> => {
  await call((client) => client.auth.logout()).catch(() => undefined)
  set({ status: "signedOut" })
}

onUnauthorized(() => set({ status: "signedOut" }))
